/**
 * Public event emitter for the `window.SalonLift.on(...)` API.
 *
 * Module-scope singleton — listeners survive across open/close cycles
 * and are not bound to any session. `off()` is idempotent. A listener
 * exception is caught and logged; it does not crash the widget.
 *
 * Emit sites live in App.tsx — calls are additive alongside the existing
 * track() / pushDataLayer() analytics so the new public API doesn't
 * replace any existing pipe.
 */

import type { Booking } from './types';

export type SalonLiftEvent =
  | {
      type: 'widget_ready';
      merchant: string;
      widgetSlug: string | null;
      sessionId: string;
    }
  | { type: 'booking_started'; serviceId: string; sessionId: string }
  // Fires when the user submits the Contact step (commits intent before
  // payment/confirm). Distinct from `booking_completed`, which fires
  // only after the server confirms the booking exists in the provider.
  | { type: 'contact_submitted'; sessionId: string }
  | { type: 'booking_completed'; booking: Booking; sessionId: string };

export type SalonLiftEventType = SalonLiftEvent['type'];

export type SalonLiftListener<K extends SalonLiftEventType> = (
  payload: Extract<SalonLiftEvent, { type: K }>,
) => void;

type AnyListener = (payload: SalonLiftEvent) => void;

const listeners: Record<SalonLiftEventType, AnyListener[]> = {
  widget_ready: [],
  booking_started: [],
  contact_submitted: [],
  booking_completed: [],
};

export function emit<E extends SalonLiftEvent>(event: E): void {
  const bucket = listeners[event.type];
  // Snapshot to a local so an off() called inside a listener doesn't
  // mutate the iteration order under us.
  for (const cb of bucket.slice()) {
    try {
      cb(event);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SalonLift] listener threw', e);
    }
  }
}

export function on<K extends SalonLiftEventType>(
  event: K,
  cb: SalonLiftListener<K>,
): () => void {
  const bucket = listeners[event];
  bucket.push(cb as AnyListener);
  return () => {
    const i = bucket.indexOf(cb as AnyListener);
    if (i >= 0) bucket.splice(i, 1);
  };
}
